import React, { useEffect, useState } from 'react'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { LoadingSpinner } from './LoadingSpinner'
import { DuplicateAreaModalMetaProps, Event } from '../data/types'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import { sortByName } from '../utils/sorting'

type Props = {
    meta: DuplicateAreaModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (options: DuplicateAreaOptions) => Promise<void>
    areaName: string
    currentEventId: number
}

export type DuplicateAreaOptions = {
    count: number
    includeAllocations: boolean
    targetEventId: number | null
}

export const DuplicateAreaModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, areaName, currentEventId } = props

    const [count, setCount] = useState<number>(1)
    const [includeAllocations, setIncludeAllocations] = useState(false)
    const [duplicateTarget, setDuplicateTarget] = useState<'same' | 'different'>('same')
    const [loading, setLoading] = useState(false)
    const [loadingEvents, setLoadingEvents] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [allEvents, setAllEvents] = useState<Event[]>([])
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null)

    useEffect(() => {
        const fetchEvents = async () => {
            if (!isOpen || duplicateTarget !== 'different') return
            if (allEvents.length > 0) return

            setLoadingEvents(true)
            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (!response.ok) throw new Error(meta.loadEventsError)
                const result = await response.json()
                let events: Event[] = result.value || result
                if (Array.isArray(events) && events.length > 0 && Array.isArray(events[0])) {
                    events = events[0]
                }
                setAllEvents(events)
            } catch {
                setError(meta.loadEventsError)
            } finally {
                setLoadingEvents(false)
            }
        }

        fetchEvents()
    }, [isOpen, duplicateTarget, allEvents.length, meta.loadEventsError])

    useEffect(() => {
        if (!isOpen) {
            setCount(1)
            setIncludeAllocations(false)
            setDuplicateTarget('same')
            setSelectedEventId(null)
            setError(null)
        }
    }, [isOpen])

    const handleEventChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const eventId = Number(e.target.value) || null
        setSelectedEventId(eventId)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (count < 1 || count > 50) {
            setError(meta.countValidationError)
            return
        }

        if (duplicateTarget === 'different' && !selectedEventId) {
            setError(meta.selectEventError)
            return
        }

        setLoading(true)
        setError(null)

        try {
            await onSubmit({
                count,
                includeAllocations,
                targetEventId: duplicateTarget === 'different' ? selectedEventId : null
            })
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    const selectableEvents = sortByName(allEvents.filter(event => event.id !== currentEventId))

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            maxWidth="md"
            footer={
                <div className="flex justify-end gap-3">
                    <Button onClick={onClose} variant="outline" disabled={loading}>
                        {meta.cancelButton}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || (duplicateTarget === 'different' && !selectedEventId)}
                        color="primary"
                    >
                        {loading ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form onSubmit={handleSubmit}>
                <div className="px-6 py-4">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {meta.description}
                    </p>

                    <div className="mb-4 bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {areaName}
                        </p>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {meta.countLabel}
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="50"
                            value={count}
                            onChange={(e) => setCount(parseInt(e.target.value) || 1)}
                            placeholder={meta.countPlaceholder}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                     focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeAllocations}
                                onChange={(e) => setIncludeAllocations(e.target.checked)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded
                                         focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                {meta.includeAllocationsLabel}
                            </span>
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ms-6">
                            {meta.includeAllocationsDescription}
                        </p>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {meta.destinationLabel}
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="duplicateAreaTarget"
                                    value="same"
                                    checked={duplicateTarget === 'same'}
                                    onChange={() => setDuplicateTarget('same')}
                                    className="w-4 h-4 text-primary-600 border-gray-300
                                             focus:ring-primary-500 dark:border-gray-600"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {meta.sameEventLabel}
                                </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="duplicateAreaTarget"
                                    value="different"
                                    checked={duplicateTarget === 'different'}
                                    onChange={() => setDuplicateTarget('different')}
                                    className="w-4 h-4 text-primary-600 border-gray-300
                                             focus:ring-primary-500 dark:border-gray-600"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {meta.differentEventLabel}
                                </span>
                            </label>
                        </div>
                    </div>

                    {duplicateTarget === 'different' && (
                        <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            {loadingEvents ? (
                                <LoadingSpinner meta={LoadingSpinnerMeta} />
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {meta.eventLabel}
                                    </label>
                                    <select
                                        value={selectedEventId ?? ''}
                                        onChange={handleEventChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                                 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    >
                                        <option value="">{meta.selectEventPlaceholder}</option>
                                        {selectableEvents.map(event => (
                                            <option key={event.id} value={event.id}>
                                                {event.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </form>
        </Modal>
    )
}
